import { AfterViewInit, Component, ElementRef, Inject, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NodeConfigurationDTO } from 'src/app/model/configuration/node-configuration-dto';
import { NodeService } from 'src/app/services/node.service';
import { SnmpService } from 'src/app/services/snmp.service';
import { MibObjectSelectorData, MibObjectSelectorDialogComponent } from '../mib-object-selector-dialog/mib-object-selector-dialog.component';
import { LoadIndicatorService } from 'src/app/services/load-indicator.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SnmpVersionCode } from 'src/app/model/configuration/snmp/snmp-version-code';
import { SnmpSecurityLevel } from 'src/app/model/configuration/snmp/snmp-security-level';
import { SnmpAuthentication } from 'src/app/model/configuration/snmp/snmp-authentication';
import { SnmpObjectConfigurationDTO } from 'src/app/model/configuration/snmp/snmp-object-configuration-dto';
import { SnmpObjectConfigurationDialogComponent } from '../snmp-object-configuration-dialog/snmp-object-configuration-dialog.component';
import { MibObject } from 'src/app/model/configuration/snmp/mib-object';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-node-configuration-dialog',
    templateUrl: './node-configuration-dialog.component.html',
    styleUrls: ['./node-configuration-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NodeConfigurationDialogComponent implements OnInit, AfterViewInit {

    @ViewChildren("mibFileSelector") fileInputs?: QueryList<ElementRef>;

    fileInput?: ElementRef;
    selectedDevice?: AbstractControl;

    form?: FormGroup;
    objects: SnmpObjectConfigurationDTO[] = [];

    // Opciones para los select
    versions = Object.keys(SnmpVersionCode).filter(key => isNaN(Number(key)));
    securityLevels = Object.keys(SnmpSecurityLevel).filter(key => isNaN(Number(key)));
    authentications = Object.keys(SnmpAuthentication).filter(key => isNaN(Number(key)));

    public configuration?: NodeConfigurationDTO;
    public mibDialogShown = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public id: number,
        public dialogRef: MatDialogRef<NodeConfigurationDialogComponent>,
        public nodeService: NodeService,
        public snmpService: SnmpService,
        public loadIndicatorService: LoadIndicatorService,
        public dialog: MatDialog,
        public fb: FormBuilder,
        private readonly toastr: ToastrService
    ) {
    }

    ngOnInit(): void {
        this.loadIndicatorService.show()
        this.nodeService.getNodeConfiguration(this.id)
            .then(dto => {
                this.configuration = dto
                if (this.configuration?.snmp) {
                    this.form = this.fb.group({
                        frequencyMinutes: [this.configuration.snmp.frequencyMinutes, Validators.required, this.requiredAsyncValidator, Validators.pattern('^[0-9]*$')],
                        timeoutSeconds: [this.configuration.snmp.timeoutSeconds, Validators.required, this.requiredAsyncValidator, Validators.pattern('^[0-9]*$')],
                        records: [this.configuration.snmp.records, Validators.required, this.requiredAsyncValidator, Validators.pattern('^[0-9]*$')],
                        separator: [this.configuration.snmp.separator],
                        devices: this.fb.array(this.configuration.snmp.devices.map(device => {
                            return this.fb.group({
                                name: [device.name, Validators.required, this.requiredAsyncValidator],
                                address: [device.address, Validators.required, this.requiredAsyncValidator, Validators.pattern('^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$')],
                                port: [device.port, Validators.required, this.requiredAsyncValidator, Validators.pattern('^[0-9]*$')],
                                version: [device.version, Validators.required, this.requiredAsyncValidator],
                                community: [device.community],
                                securityLevel: [device.securityLevel],
                                authentication: [device.authentication],
                                authenticationPhrase: [device.authenticationPhrase],
                                privacyPhrase: [device.privacyPhrase],
                                user: [device.user],
                                context: [device.context],
                                objects: this.fb.array(device.objects.map(obj => {
                                    return this.fb.group({
                                        name: [obj.name, Validators.required, this.requiredAsyncValidator],
                                        oid: [obj.oid, Validators.required, this.requiredAsyncValidator],
                                    })
                                })),
                            })
                        }))
                    })
                }
            }).catch(error => {
                this.toastr.error('Node data is not valid or cannot establish a connection.')
            }).finally(() => {
                this.loadIndicatorService.hide()
            })
    }

    ngAfterViewInit(): void {
        const context = this
        this.fileInputs?.changes.subscribe(x => {
            if (x.length) {
                context.fileInput = x.first
            }
        })
    }

    // Getter para acceder al FormArray de dispositivos
    get devices(): FormArray {
        return this.form?.controls['devices'] as FormArray;
    }

    // Getter para acceder al FormArray de dispositivos
    getObjects(device: AbstractControl): FormArray {
        return device.get('objects') as FormArray;
    }

    editObjectConfiguration(object: AbstractControl, device?: AbstractControl) {
        const o: SnmpObjectConfigurationDTO = {
            name: object.get('name')?.value,
            oid: object.get('oid')?.value,
        }
        const dialogRef = this.dialog.open(SnmpObjectConfigurationDialogComponent, {
            data: { item: o },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== null) {
                object.get('name')?.setValue(result.name);
                object.get('oid')?.setValue(result.oid);
                if (device) {
                    const objects = device.get('objects') as FormArray;
                    objects.push(object)
                }
            }
        })
    }

    deleteObject(device: AbstractControl, index: number) {
        this.getObjects(device).removeAt(index)
    }

    addObject(device: AbstractControl) {
        const newObject = this.fb.group({
            name: ['', Validators.required, this.requiredAsyncValidator],
            oid: ['', Validators.required, this.requiredAsyncValidator],
        })
        this.editObjectConfiguration(newObject, device)
    }

    addDevice() {
        const newObject = this.fb.group({
            name: ['', Validators.required],
            address: ['', Validators.required, Validators.pattern('^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$')],
            port: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
            version: ['', Validators.required],
            community: [''],
            securityLevel: [''],
            authentication: [''],
            authenticationPhrase: [''],
            privacyPhrase: [''],
            user: [''],
            context: [''],
            objects: this.fb.array([])
        })

        this.devices.push(newObject)
    }

    public loadMib(device: AbstractControl): void {
        this.selectedDevice = device
        this.fileInput?.nativeElement.click()
    }

    public fileSelected(event: any): void {
        this.loadIndicatorService.show()

        let file = event.target?.files[0]
        let device = this.selectedDevice!

        const formData = new FormData();
        formData.append("file", file);

        this.snmpService.uploadMibFile(formData)
            .then(dto => {
                let objects = this.getObjects(device).value as SnmpObjectConfigurationDTO[]
                const data: MibObjectSelectorData = {
                    file: file.name,
                    configuredObjects: objects,
                    mib: dto
                }
                const dialogRef = this.dialog.open(MibObjectSelectorDialogComponent, {
                    data: data,
                });
                this.mibDialogShown = true;

                dialogRef.afterClosed().subscribe((result: MibObject[]) => {
                    this.mibDialogShown = false;
                    this.fileInput!.nativeElement.value = null
                    if (result !== null) {
                        const newObjects = result.filter(o => this.isAddedObject(objects, result, o))
                        newObjects.forEach((obj, index) => {
                            const newObject = this.fb.group({
                                name: [obj.name, Validators.required],
                                oid: [obj.oid, Validators.required],
                            })
                            this.getObjects(device).push(newObject)
                        })

                    }
                })
            }).catch(e => {
                this.toastr.error('Error uploading mib file')
            })
            .finally(() => {
                this.loadIndicatorService.hide()
            })
    }

    isAddedObject(configuredObjects: SnmpObjectConfigurationDTO[], selectedObjects: MibObject[], object: MibObject): boolean {
        return configuredObjects.findIndex(co => co.oid === object.oid) === -1 && selectedObjects.findIndex(so => so.oid === object.oid) >= 0
    }

    public save(): void {
        if (this.form!.valid) {
            this.configuration!.snmp = this.form!.value
            this.nodeService.saveNodeConfiguration(this.id, this.configuration!)
                .then(() => {
                    this.dialogRef.close()
                }).catch(() => {
                    this.toastr.error('Error saving configuration')
                })
        }
    }

    public cancel(): void {
        this.dialogRef.close()
    }

    public deleteDevice(i: number): void {
        this.devices.removeAt(i)
    }

    requiredAsyncValidator(control: AbstractControl): Observable<ValidationErrors | null> {
        return new Observable(observer => {
            // Simulamos una verificación asíncrona
            setTimeout(() => {
                // Comprobar si el valor es válido, ya sea una cadena no vacía o un número distinto de 0
                if (control.value != null && (typeof control.value === 'string' ? control.value.trim() !== '' : control.value > 0)) {
                    observer.next(null); // El campo es válido
                } else {
                    observer.next({ required: true }); // El campo es obligatorio
                }
                observer.complete();
            }, 1000); // Simula un retraso de 1 segundo
        });
    }
}
